"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import * as React from "react";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ReasoningContextValue {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration?: number;
}

const ReasoningContext = React.createContext<ReasoningContextValue | null>(null);

export function useReasoning() {
  const ctx = React.useContext(ReasoningContext);
  if (!ctx)
    throw new Error("Reasoning components must be used inside <Reasoning>");
  return ctx;
}

// ---------------------------------------------------------------------------
// <Reasoning />
// ---------------------------------------------------------------------------

interface ReasoningProps extends React.ComponentProps<typeof Collapsible> {
  isStreaming?: boolean;
  duration?: number;
}

export function Reasoning({
  children,
  className,
  isStreaming = false,
  duration: externalDuration,
  ...props
}: ReasoningProps) {
  const [isOpen, setIsOpen] = React.useState(isStreaming);
  const [internalDuration, setInternalDuration] = React.useState(0);
  const startTimeRef = React.useRef<number | null>(null);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  React.useEffect(() => {
    if (isStreaming) {
      setIsOpen(true);
      startTimeRef.current = Date.now();
      intervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setInternalDuration(
            Math.round((Date.now() - startTimeRef.current) / 1000)
          );
        }
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      const timeout = setTimeout(() => {
        if (!isStreaming) setIsOpen(false);
      }, 500);
      return () => clearTimeout(timeout);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isStreaming]);

  const duration = externalDuration ?? internalDuration;

  return (
    <ReasoningContext.Provider value={{ isStreaming, isOpen, setIsOpen, duration }}>
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className={cn("w-full", className)}
        {...props}
      >
        {children}
      </Collapsible>
    </ReasoningContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// <ReasoningTrigger />
// ---------------------------------------------------------------------------

interface ReasoningTriggerProps
  extends React.ComponentProps<typeof CollapsibleTrigger> {
  getThinkingMessage?: (
    isStreaming: boolean,
    duration?: number
  ) => React.ReactNode;
}

export function ReasoningTrigger({
  className,
  getThinkingMessage,
  ...props
}: ReasoningTriggerProps) {
  const { isStreaming, duration } = useReasoning();

  const formatDuration = (seconds?: number) => {
    if (!seconds || seconds === 0) return "um momento";
    if (seconds < 60) return `${seconds} segundo${seconds !== 1 ? "s" : ""}`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const defaultMessage = isStreaming ? (
    <span className="inline-flex items-center gap-1">
      Pensando
      <span className="inline-flex gap-[2px]">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="inline-block w-[3px] h-[3px] rounded-full bg-[color:var(--nt-primary)] animate-pulse"
            style={{ animationDelay: `${i * 200}ms` }}
          />
        ))}
      </span>
      {(duration ?? 0) > 0 && (
        <span className="text-[color:var(--foreground-muted)] font-normal ml-1">
          {duration}s
        </span>
      )}
    </span>
  ) : (
    `Pensou por ${formatDuration(duration ?? 0)}`
  );

  return (
    <CollapsibleTrigger
      className={cn(
        "group inline-flex items-center gap-2 rounded-md px-0 py-1.5 text-sm transition-colors",
        "text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)]",
        className
      )}
      {...props}
    >
      {/* Chevron */}
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[color:var(--foreground-muted)] transition-transform duration-200 group-data-[state=open]:rotate-90" />

      {/* Brain Icon */}
      <svg
        className="h-4 w-4 shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
        <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
        <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
        <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
        <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
        <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
        <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
        <path d="M6 18a4 4 0 0 1-1.967-.516" />
        <path d="M19.967 17.484A4 4 0 0 1 18 18" />
      </svg>

      {/* Streaming indicator dot */}
      {isStreaming && (
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--nt-primary)] opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[color:var(--nt-primary)]" />
        </span>
      )}

      {/* Label */}
      <span className="font-medium text-[13px]">
        {getThinkingMessage
          ? getThinkingMessage(isStreaming, duration)
          : defaultMessage}
      </span>
    </CollapsibleTrigger>
  );
}

// ---------------------------------------------------------------------------
// <ReasoningContent />
// ---------------------------------------------------------------------------

export function ReasoningContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  const { isStreaming } = useReasoning();

  return (
    <CollapsibleContent
      className={cn(
        "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        className
      )}
      {...props}
    >
      <div
        className={cn(
          "mt-1 ml-[7px] pl-4 border-l-2 py-2 text-sm text-[color:var(--foreground-muted)] whitespace-pre-wrap leading-relaxed",
          isStreaming
            ? "border-[color:var(--nt-primary)]/50"
            : "border-[color:var(--card-border)]"
        )}
      >
        {children}
      </div>
    </CollapsibleContent>
  );
}
